import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SOCIALS, ENQUIRY_SERVICES } from "@/content";
import { Reveal } from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors duration-300 focus:border-white/40";

const SocialIcon = ({ label }) => {
  if (label === "Telegram") return <Send className="h-4 w-4" />;
  return <span className="text-sm font-bold leading-none">𝕏</span>;
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", service: ENQUIRY_SERVICES[0], message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPrefill = (e) => {
      const { service, item } = e.detail;
      setForm((f) => ({
        ...f,
        service: ENQUIRY_SERVICES.includes(service) ? service : f.service,
        message: item ? `I'd like to book: ${item}` : f.message,
      }));
      document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
      setTimeout(
        () => document.querySelector('[data-testid="enquiry-name-input"]')?.focus(),
        700
      );
    };
    window.addEventListener("prefill-enquiry", onPrefill);
    return () => window.removeEventListener("prefill-enquiry", onPrefill);
  }, []);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Enquiry received — check your inbox for a confirmation email.");
      setForm({ name: "", email: "", service: ENQUIRY_SERVICES[0], message: "" });
    } catch {
      toast.error("Something went wrong. Please email us directly instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative scroll-mt-28 border-t border-white/10 py-24 sm:py-32">
      <div className="relative mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-zinc-400">Get in touch</p>
          <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold uppercase tracking-tight sm:text-4xl lg:text-5xl">
            Reach <span className="text-zinc-500">our team.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
            Membership enquiries, partnerships and press — every message lands
            directly with our executive team.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="flex flex-col gap-6 lg:col-span-2">
            <Reveal delay={0.05}>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-[border-color,background-color] duration-500 hover:border-white/40 hover:bg-white/[0.04]"
              >
                <div className="flex items-center gap-4">
                  <Mail className="h-5 w-5 shrink-0 text-zinc-500 transition-colors duration-500 group-hover:text-white" />
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Email</p>
                    <p className="mt-1 break-all text-sm font-semibold">{CONTACT.email}</p>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-zinc-500 transition-[color,transform] duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white" />
              </a>
            </Reveal>

            <Reveal delay={0.1}>
              <div data-testid="contact-phone-card" className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <Phone className="h-5 w-5 shrink-0 text-zinc-500" />
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Phone</p>
                  <p className="mt-1 text-sm font-semibold">{CONTACT.phone}</p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div data-testid="contact-office-card" className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <MapPin className="h-5 w-5 shrink-0 text-zinc-500" />
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Office</p>
                  <p className="mt-1 text-sm font-semibold">{CONTACT.office}</p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div data-testid="contact-socials-card" className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Connect</p>
                <div className="flex gap-3">
                  {SOCIALS.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={social.testid}
                      aria-label={`Desires After Hours on ${social.label}`}
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-zinc-300 transition-[border-color,color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-white/60 hover:bg-white hover:text-black"
                    >
                      <SocialIcon label={social.label} />
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              data-testid="enquiry-form"
              className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-8"
            >
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Enquiry</p>
              <h3 className="mt-3 font-display text-xl font-bold tracking-tight sm:text-2xl">
                Tell us what you're looking for
              </h3>

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  data-testid="enquiry-name-input"
                  className={inputClass}
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="Your email"
                  data-testid="enquiry-email-input"
                  className={inputClass}
                />
              </div>

              <select
                value={form.service}
                onChange={update("service")}
                data-testid="enquiry-service-select"
                className={`mt-4 ${inputClass} appearance-none bg-black`}
              >
                {ENQUIRY_SERVICES.map((service) => (
                  <option key={service} value={service} className="bg-black">
                    {service}
                  </option>
                ))}
              </select>

              <textarea
                value={form.message}
                onChange={update("message")}
                placeholder="Anything else we should know? (optional)"
                rows={4}
                data-testid="enquiry-message-input"
                className={`mt-4 ${inputClass} resize-none`}
              />

              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-submit-button"
                className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-white px-7 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-black transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-zinc-300 disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send Enquiry"} <Send className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
